import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, Check, Loader2, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { getSchedule, trackFromSchedule } from "../lib/api";

const DAYS = [
  { key: "monday",    short: "Mon", full: "Monday" },
  { key: "tuesday",   short: "Tue", full: "Tuesday" },
  { key: "wednesday", short: "Wed", full: "Wednesday" },
  { key: "thursday",  short: "Thu", full: "Thursday" },
  { key: "friday",    short: "Fri", full: "Friday" },
  { key: "saturday",  short: "Sat", full: "Saturday" },
  { key: "sunday",    short: "Sun", full: "Sunday" },
];

function todayKey() {
  // Local weekday → schedule key. `en-US` ensures "Monday", "Tuesday", ...
  const w = new Date().toLocaleDateString("en-US", { weekday: "long" }).toLowerCase();
  return DAYS.find((d) => d.key === w)?.key || "monday";
}

// Convert HH:MM (UTC) to viewer-local HH:MM
function formatLocalTime(utcHHMM) {
  if (!utcHHMM) return "";
  const [h, m] = utcHHMM.split(":").map(Number);
  const d = new Date();
  d.setUTCHours(h, m, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

function ScheduleCard({ entry, tracked, onAdd, adding }) {
  const localTime = formatLocalTime(entry.airing_time_utc);
  return (
    <article
      data-testid={`schedule-card-${entry.mal_id}`}
      className="group relative flex flex-col overflow-hidden border border-white/10 bg-neutral-950 transition-all hover:border-[#FF3B30]"
    >
      <Link to={`/anime/${entry.mal_id}`} className="relative block aspect-[3/4] overflow-hidden bg-neutral-900">
        {entry.image_url && (
          <img
            src={entry.image_url}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
        {tracked && (
          <span
            data-testid={`schedule-tracked-badge-${entry.mal_id}`}
            className="absolute left-2 top-2 flex items-center gap-1 border border-emerald-500/60 bg-emerald-500/15 px-2 py-1 font-mono text-[9px] uppercase tracking-widest text-emerald-300 backdrop-blur"
          >
            <Check className="h-3 w-3" /> Tracked
          </span>
        )}
        {entry.score != null && (
          <span className="absolute right-2 top-2 flex items-center gap-1 border border-white/25 bg-black/60 px-2 py-1 font-mono text-[10px] text-white backdrop-blur">
            <Star className="h-3 w-3 text-amber-400" fill="currentColor" /> {entry.score.toFixed(1)}
          </span>
        )}
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2 font-mono text-[9px] uppercase tracking-widest text-white">
          <span className="border border-white/25 bg-black/60 px-2 py-1 backdrop-blur">
            EP {entry.next_episode ?? "?"}
          </span>
          <span className="border border-white/25 bg-black/60 px-2 py-1 backdrop-blur">
            {localTime}
          </span>
        </div>
      </Link>

      <div className="flex flex-1 flex-col gap-2 p-3">
        <h3 className="line-clamp-2 min-h-[2.5rem] font-semibold leading-tight text-white group-hover:text-[#FF3B30]">
          {entry.title}
        </h3>
        <p className="line-clamp-1 font-mono text-[9px] uppercase tracking-widest text-neutral-500">
          {(entry.genres || []).slice(0, 3).join(" · ") || "—"}
        </p>
        {tracked ? (
          <div
            data-testid={`schedule-tracked-inline-${entry.mal_id}`}
            className="mt-1 flex items-center justify-center gap-1.5 border border-emerald-500/40 bg-emerald-500/5 px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-emerald-300"
          >
            <Check className="h-3 w-3" /> In catalogue
          </div>
        ) : (
          <button
            data-testid={`schedule-add-btn-${entry.mal_id}`}
            onClick={onAdd}
            disabled={adding}
            className="mt-1 flex items-center justify-center gap-1.5 border border-white/20 bg-[#FF3B30] px-2 py-2 font-mono text-[10px] uppercase tracking-widest text-white transition-colors hover:bg-white hover:text-black disabled:opacity-50"
          >
            {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            {adding ? "Adding..." : "Track"}
          </button>
        )}
      </div>
    </article>
  );
}

export default function Schedule() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(todayKey());
  const [addingId, setAddingId] = useState(null);
  const [tracked, setTracked] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getSchedule("all");
        if (cancelled) return;
        setData(res);
        setTracked(new Set(res.tracked_mal_ids || []));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't load the schedule");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const currentDay = useMemo(
    () => data?.days?.find((d) => d.day === selected),
    [data, selected]
  );

  const handleAdd = async (entry) => {
    setAddingId(entry.mal_id);
    try {
      await trackFromSchedule(entry.mal_id, "watching");
      setTracked((prev) => new Set(prev).add(entry.mal_id));
      toast.success(`Tracking ${entry.title}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't add to catalogue");
    } finally {
      setAddingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF3B30]" />
      </div>
    );
  }

  const totalWeek = data?.total || 0;

  return (
    <div className="mx-auto max-w-[1600px] px-6 py-10 md:px-10" data-testid="schedule-page">
      <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-[#FF3B30]">// Vol. 10</p>
      <h1 className="mt-2 font-display text-6xl uppercase leading-[0.9] tracking-tight text-white md:text-8xl">
        Airing<br />This Week
      </h1>
      <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-neutral-400">
        <span className="flex items-center gap-2">
          <CalendarClock className="h-4 w-4 text-[#FF3B30]" />
          {totalWeek} shows currently airing
        </span>
        <span className="font-mono text-[10px] uppercase tracking-widest text-neutral-600">
          Times shown in your local timezone
        </span>
      </div>

      {/* Day selector */}
      <div
        role="tablist"
        className="sticky top-16 z-30 mt-8 -mx-6 flex gap-1 overflow-x-auto border-b border-white/10 bg-black/80 px-6 py-2 backdrop-blur-md md:-mx-10 md:px-10"
      >
        {DAYS.map((d) => {
          const count = data?.days?.find((x) => x.day === d.key)?.count || 0;
          const isSel = d.key === selected;
          const isToday = d.key === todayKey();
          return (
            <button
              key={d.key}
              role="tab"
              aria-selected={isSel}
              data-testid={`schedule-day-tab-${d.key}`}
              onClick={() => setSelected(d.key)}
              className={`flex flex-shrink-0 flex-col items-center gap-0.5 border px-3 py-2 font-mono text-[10px] uppercase tracking-widest transition-colors md:px-5 ${
                isSel
                  ? "border-[#FF3B30] bg-[#FF3B30] text-white"
                  : "border-white/10 text-neutral-500 hover:border-white/30 hover:text-white"
              }`}
            >
              <span className="flex items-center gap-2">
                {d.short}
                {isToday && (
                  <span className={`text-[8px] ${isSel ? "text-white/90" : "text-[#FF3B30]"}`}>
                    · TODAY
                  </span>
                )}
              </span>
              <span className={`text-[9px] ${isSel ? "text-white/70" : "text-neutral-600"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Cards */}
      <div className="mt-8">
        {!currentDay || currentDay.count === 0 ? (
          <div
            data-testid="schedule-empty-day"
            className="border border-dashed border-white/15 py-24 text-center"
          >
            <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-neutral-600">// Quiet day</p>
            <h2 className="mt-2 font-display text-4xl uppercase text-white">
              Nothing airing on {DAYS.find((d) => d.key === selected)?.full}
            </h2>
            <p className="mt-3 text-sm text-neutral-500">
              Pick another day above — {totalWeek} shows are airing this week.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
            {currentDay.entries.map((entry) => (
              <ScheduleCard
                key={entry.mal_id}
                entry={entry}
                tracked={tracked.has(entry.mal_id)}
                adding={addingId === entry.mal_id}
                onAdd={() => handleAdd(entry)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
